import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ValidatorInput } from '../../../../model.module/validation/validation-descriptor.entity';
import { AppConstants } from '../../../../app-constants.service';
import { FileUploadService } from '../../../../services.module/upload.service';

import { Tuple2 } from '../../../../model.module/tuple2.model';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PipeFunctions } from '../../../../pipes.module/_pipe.functions';
import { ErrorObservable, MessageObservable } from '../../../../services.module/message.observable';
import { ErrorModel, ErrorSeverity, MessageModel } from '../../../../model.module/modals.model';
import { BuergschaftsAntragType, BuergschaftsAntragTypes } from '../../../../model.module/bgsch-antrag-type.model';
import { FileUploadComponent } from '../../../../elements.component.module/file-upload.component';
import { AuthService } from '../../../../services.module/auth.service';
import { ClientDownloadService } from '../../../../services.module/client-download.service';
import { DateUtilsService } from '../../../../common.component.module/date-utils.service';
import { CreateBuergscheinModel } from '../../../../model.module/create-buergschein.model';
import { BuergscheineApiService } from '../../../../../generated/api/buergscheine/buergscheine.service';
import { VertragApiService } from '../../../../../generated/api/vertrag/vertrag.service';
import { JsonFilesApiService } from '../../../../../generated/api/json-files/json-files.service';
import { UserApiService } from '../../../../../generated/api/user/user.service';
import { PartnerApiService } from '../../../../../generated/api/partner/partner.service';
import { ConfigurationApiService } from '../../../../../generated/api/configuration/configuration.service';
import { Animator } from '../../../../providers.module/_animator';
import { AuftraggeberApiService } from '../../../../../generated/api/auftraggeber/auftraggeber.service';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin, take } from 'rxjs';
import { Land } from '../../../../model.module/json/land.model';
import {
    Auftraggeber,
    AuftraggeberSucheParams,
    BuergschaftsTemplate,
    VertragHandle
} from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import {
    BuergschaftsantragApiService
} from '../../../../../generated/api/buergschaftsantrag/buergschaftsantrag.service';
import { VertraegeService } from '../../../../providers.module/vertraege.service';
import { BuergscheineService } from '../../../../providers.module/buergscheine.service';
import { UserService } from '../../../../providers.module/user.service';
import { PartnerService } from '../../../../providers.module/partner.service';

@Component({
    selector: 'bgsch-create',
    templateUrl: './bgsch-create.component.html',
    styleUrls: ['./bgsch-create.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
class BuergschaftCreateComponent implements OnInit, OnDestroy {

    readonly STATE_1_ANTRAGSTELLER = 1;
    readonly STATE_2_AUFTRAGGEBER = 2;
    readonly STATE_3_BUERGSCHAFT = 3;
    readonly STATE_4_ABSCHLUSS = 4;
    readonly STATE_5_DANKESSEITE = 5;

    readonly AGTYP_FIRMA = 'Firma';
    readonly AGTYP_OEFFENTLICH = 'Öffentlicher Auftraggeber';
    readonly AGTYP_PRIVAT = 'Privatperson';

    readonly BUERGSCHAFTSARTEN_DIGITAL = ['MA', 'VE', 'VA', 'AF'];

    /** Aktuelle Seite im BS-Antrags-Wizard */
    createState: number = this.STATE_1_ANTRAGSTELLER;

    /** Kapsel-Datentyp für die vom Nutzer getätigten Eingaben */
    model: CreateBuergscheinModel = new CreateBuergscheinModel();

    /* Upload-Logik */

    @ViewChild('uploadField0')
    uploadField0: FileUploadComponent;

    @ViewChild('uploadField1')
    uploadField1: FileUploadComponent;

    @ViewChild('uploadField2')
    uploadField2: FileUploadComponent;

    fileNames: string[] = ['', '', ''];

    numUploadFiles = 0;

    currentFileIndex = 0;
    uploadedPercent: number[] = [];

    done = false;
    lastFileIndex = 0;
    errorMessages: string[] = [null, null, null];
    errorMessagesMask = 0;

    showNoUploadMessage = false;

    /* Upload-Logik Ende */

    urlBuergscheinAntragBedingungenPdf: string;

    isAenderungsantrag = false;

    isAenderungsantragAuswahlGesperrt = false;

    vertragsdatumEntered = false;

    currFormularType: BuergschaftsAntragType = BuergschaftsAntragTypes.findByBuergschaftsart('AF');

    visibleFormulare: Tuple2<string, string>[] = this.getFormulare();

    auftraggeberLaender: Land[] = [];

    befristetSichtbarkeit: boolean;

    nurBefristet: boolean;

    nurUnbefristet: boolean;

    buergschaftBeantragenBtnDisabled = false;

    buergscheinId: string;
    vsnr: string;
    alter: string;

    aenderungsBuergschaften: string[] = [];

    weitereBuergschaftszusaetze = false;

    bspTemplates: BuergschaftsTemplate[] = [];

    selectedBspTemplate: BuergschaftsTemplate;

    isForcedDigitalAuftraggeberCheckPending = false;
    forcedAuftraggeberInputBackup: Auftraggeber;

    /**
     * Please have a look at CreateBuergscheinRequest in model.module/xhr/request/create-buergschein.model
     * The label here are CreateBuergscheinInput.label. And there are many instances of CreateBuergscheinInput.label
     * in CreateBuergscheinRequest.
     *
     * We need to have it here, because the validator needs to know, when to stop validating. Otherwise
     * it would validate future fields and the user never could come to a futher page.
     */
    private readonly lastFieldsNameOfPage: string[] = [
        null, // there is no page 0
        'vertrag', // last label in page 1
        'forcedAuftraggeberValidator', // last label in page 2
        'buergschaftsformat', // last label in page 3
        '' // page 4
    ];

    private _vertragHandles: VertragHandle[] = [];

    /**
     * Map, die es erlaubt anhand der verkürzten Vertragsnummern die vollständige zu rekonstruieren
     */
    private readonly rigidVsnrToVertragHandleMap: Map<string, VertragHandle> = new Map();

    constructor(
        public readonly dateUtilsService: DateUtilsService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly appConstants: AppConstants,
        private readonly messageObservable: MessageObservable,
        private readonly errorObservable: ErrorObservable,
        private readonly authService: AuthService,
        private readonly fileUploadService: FileUploadService,
        private readonly clientDownloadService: ClientDownloadService,
        private readonly auftraggeberApiService: AuftraggeberApiService,
        private readonly buergscheineApiService: BuergscheineApiService,
        private readonly vertragApiService: VertragApiService,
        private readonly buergschaftsantragApiService: BuergschaftsantragApiService,
        private readonly jsonFilesApiService: JsonFilesApiService,
        private readonly userApiService: UserApiService,
        private readonly partnerApiService: PartnerApiService,
        private readonly configurationApiService: ConfigurationApiService,
        private readonly vertraegeService: VertraegeService,
        private readonly buergscheineService: BuergscheineService,
        private readonly userService: UserService,
        private readonly partnerService: PartnerService
    ) {
        this.configurationApiService.getConfiguration().subscribe(configuration => {
            this.urlBuergscheinAntragBedingungenPdf = configuration.urlBuergscheinAntragBedingungenPdf;
        });

        this.fileUploadService = fileUploadService.forData(
            '/buergschaftsantrag/templateUpload',
            3,
            -1,
            appConstants.bsAntragMaxFileSumSize,
            false,
            appConstants.bgschCreateFileFilter,
            appConstants.messages.bgAntragFremdformulareFileTypeMsg,
            this.onProgress,
            this.onAdded,
            this.onUploaded,
            this.onError,
        );
    }

    ngOnInit() {
        this.parseQueryString();

        this.initialize();
    }

    ngOnDestroy() {
        this.fileUploadService.cleanup();
    }

    /**
     * Query-Parameter aus dem Request auswerten und als Properties vorhalten
     */
    private parseQueryString() {
        this.activatedRoute.queryParams.subscribe((params: Params) => {
            // Vorlage gegeben
            if ('bgsch' in params) {
                this.buergscheinId = params['bgsch'];
            }

            if ('vsnr' in params) {
                this.vsnr = params['vsnr'];
            }

            if ('alter' in params) {
                this.alter = params['alter'];
                this.isAenderungsantragAuswahlGesperrt = this.alter === '1';
            }
        });
    }

    /**
     * Setze alle Werte in den Initialstatus zurück.
     * Bereite Antrag vor, je nach Vorgabe der Query-Parameter.
     */
    private initialize(): void {
        this.isAenderungsantrag = this.alter === '1';

        if (this.buergscheinId && this.vsnr) {
            this.prepareAenderungsantrag();
        } else {
            this.prepareNeuantrag();
        }

        this.checkBefristung();

        this.ladeAuswahllisteVertraegeDaten();
        this.ladeAuswahllisteLaenderDaten();
    }

    /**
     * Sende ausgefüllten Bürgschaftsantrag ab.
     * Diese Methode wird nach Klick auf "Antrag absenden" aufgerufen
     */
    sendData(): void {
        this.buergschaftBeantragenBtnDisabled = true;

        const validationResult = this.model.validate(this.lastFieldsNameOfPage[this.STATE_4_ABSCHLUSS]);
        const valid = validationResult === null;

        if (!valid) {
            this.buergschaftBeantragenBtnDisabled = false;

            this.messageObservable.notify(new MessageModel(
                'Bitte korrigieren Sie Ihre Angaben.',
                validationResult.map(vr => vr.message),
            ));

            return;
        }

        Animator.addRequest();

        if (this.model.gewuenschtesFormular.value !== 'SF00') {
            this.resetAllUploadFields();
        }

        if (this.fileUploadService.getFileCount() !== 0) {
            this.numUploadFiles = this.fileUploadService.getFileCount();
            this.fileUploadService.startUpload();
        } else {
            // Bei FileUploads ruft der FileUploadService die Funktion finishAntrag per Callback auf,
            // wird im Konstruktor uebergeben
            this.finishAntrag();
        }
    }

    /**
     * Handle Klicken auf den Zurück-Button
     */
    goBack(): void {
        history.back();
    }

    /**
     * Wenn die Bürgschaftsart geändert wurde müssen die darunter liegenden Felder aktualisiert werden
     */
    updateBuergschaftsartAbhaengigeFelder(): void {
        const candidate: BuergschaftsAntragType = BuergschaftsAntragTypes.findByBuergschaftsart(this.model.buergschaftsArt.value);

        if (candidate) {
            this.currFormularType = candidate;
            this.visibleFormulare = this.getFormulare();

            // Wenn es nur ein Formular gibt, wähle dieses aus
            if (candidate.formulare && this.visibleFormulare.length === 1) {
                this.model.gewuenschtesFormular.value = this.visibleFormulare[0].left;
            }

            if (candidate.immerBefristet === true) {
                this.model.frist.value = 'befristet';
            }
        }

        if (!this.currFormularType.formulare.find(f => f.left === this.model.gewuenschtesFormular.value)) {
            this.model.gewuenschtesFormular.value = '';
            this.resetAllUploadFields();
        }

        this.checkBefristung();

        if (this.BUERGSCHAFTSARTEN_DIGITAL.includes(this.currFormularType.buergschaftsart)) {
            // Digitale Bürgschaft möglich; lade Templatevorschläge von Trustlog
            this.loadBspTemplatevorschlaege();
        } else {
            // Ansonsten nur analoge Bürgschaft möglich
            this.bspTemplates = [];
            this.fillFieldValue(this.model.templateSpKennung, '');
            this.templateUpdated();
        }

        this.updateShowVersandartAuswahl();
    }

    /**
     * Auswahl sichtbar wenn alle der folgenden Kriterien erfüllt sind
     * - Kein digitales Auftraggebertemplate ausgewählt
     * - Auftraggeber aus Deutschland
     * - Die Bürgschaftsart ist eine Mängelansprüche, Ausführung, Vertragserfüllungs- oder Vorauszahlungsbürgschaft ( = keine PDF für Bietung und Bauhandwerkersicherung)
     * - Der Bürgschaftstext ist KEIN "Sonderformular des Auftraggebers" ( = öffentliche und VHV-Text zulässig )
     * - Wenn keine Bürgschaftszusätze ausgewählt
     */
    public updateShowVersandartAuswahl() {
        this.model.versandartAuswahlAvailable =
            // Kein digitales Auftraggebertemplate ausgewählt
            !this.model.templateSpKennung.value
            // und Auftraggeber aus Deutschland
            && this.model.auftraggeberLand.value === 'DE'
            // und die Bürgschaftsart ist eine Mängelansprüche, Ausführung, Vertragserfüllungs- oder Vorauszahlungsbürgschaft
            && ['MA', 'AF', 'VE', 'VA'].includes(this.model.buergschaftsArt.value)
            // und der Bürgschaftstext ist KEIN "Sonderformular des Auftraggebers"
            && this.model.gewuenschtesFormular.value !== 'SF00'
            // und wenn keine Bürgschaftszusätze ausgewählt
            && this.isNoBuergschaftszusaetze();
    }

    private isNoBuergschaftszusaetze() {
        // Kein Formular der VHV? Dann sind die BS-Zusätze eh egal
        if (!(['NEIN', 'VO30', 'SF01'].includes(this.model.gewuenschtesFormular.value))) {
            return true;
        }

        // Ansonsten gib zurück, ob keine BS-Zusätze ausgewählt wurden oder nicht
        return !(
               this.model.checkboxHinterlegung.value
            || this.model.checkboxBGB768.value
            || this.model.checkboxSGBVII150Abs3.value
            || this.model.checkboxSGBIV28eAbs3a.value
            || this.model.checkboxSicherheitseinbehalt.value
            || this.model.checkboxAentG14.value
        );
    }

    public templateUpdated(): void {
        // wenn <null> ausgewählt -> Reset Template
        if (!this.model.templateSpKennung.value) {
            this.selectedBspTemplate = null;
            this.model.templateSpBeschreibung = null;

            this.updateShowVersandartAuswahl();

            return;
        }

        // Finde Template
        // ODOT Sollte nicht lineare Suche sein sondern Map
        let template = this.bspTemplates.find(value => value.spKennung === this.model.templateSpKennung.value);
        if (!template) {
            throw new Error('Template nicht gefunden');
        }

        this.selectedBspTemplate = template;
        this.model.templateSpBeschreibung = this.selectedBspTemplate.beschreibungAuftraggeber;

        this.model.gewuenschtesFormular.value = '';
        // Resette Upload-Felder und BS-Zusätze
        this.resetAllUploadFields();
        this.isGewuenschteZusaetzeMoeglich();

        // Befristung kann durch Template auf "zu befristen" und "befristen" vorbelegt sein
        this.updateBefristet();

        // Übernehme Voreinstellung Rechtsgrundlage
        if (['VOB', 'BGB'].includes(template.recht)) {
            this.fillFieldValue(this.model.gesetzlGrundlage, template.recht);
        }

        // Versandart könnte sich geändert haben
        this.updateShowVersandartAuswahl();
    }

    /**
     * Aktualisiere Sichtbarkeit/Vorauswahl der Befristungs-Felder
     */
    updateBefristet(): void {
        // Initialer Zustand
        this.nurBefristet = false;
        this.nurUnbefristet = false;
        this.fillFieldValue(this.model.frist, 'unbefristet');

        // Bietungsbürgschaften sollen immer befristet sein
        if (this.model.buergschaftsArt.value === 'BI') {
            this.nurBefristet = true;
            this.nurUnbefristet = false;
            this.fillFieldValue(this.model.frist, 'befristet');

            return;
        }

        // Alle öffentlichen Texte (d.h. alle nicht NEIN, SF01, VO30, SF00) können nur unbefristet sein
        if (!['NEIN', 'SF01', 'VO30', 'SF00'].includes(this.model.gewuenschtesFormular.value)) {
            this.nurUnbefristet = true;
            this.nurBefristet = false;
            this.fillFieldValue(this.model.frist, 'unbefristet');
        }

        // Wenn digitale Bürgschaft, dann den von der Schnittstelle übergebenen Wert für die Befristung vorbelegen.
        // Zusätzlich gilt:
        // - Wenn Auftraggebertext dann Befristung *nicht* änderbar
        // - Wenn öffentlicher Text dann Befristung *nicht* änderbar
        // - Wenn Bürgentext dann Befristung änderbar
        if (this.selectedBspTemplate && !this.model.moechteAbweichendenBuergschaftstext) {

            const befristungWert = this.selectedBspTemplate.befristung
                ? 'befristet'
                : 'unbefristet';

            // Nur Bürgentexte sollen änderbar sein, bei den anderen Templatetypen muss die Vorbelegung übernommen werden
            if (this.selectedBspTemplate.templatetyp !== 'BUE') {
                this.nurUnbefristet = !this.selectedBspTemplate.befristung;
                this.nurBefristet = this.selectedBspTemplate.befristung;
            } else {
                this.nurBefristet = false;
                this.nurUnbefristet = false;
            }

            this.fillFieldValue(this.model.frist, befristungWert);
        }
    }

    /**
     * Lädt das ausgewählte Template als PDF (authentifiziert via HttpClient/Interceptor)
     * und öffnet es in einem neuen Tab.
     */
    onVorschauTemplateClick(): void {
        if (!this.selectedBspTemplate) {
            return;
        }

        this.clientDownloadService.openPdfInNewTab(
            this.auftraggeberApiService.templateContent(this.selectedBspTemplate.spKennung)
        );
    }

    /**
     * Vorschau-Button deaktiviert, wenn kein Template ausgewählt ist
     */
    getVorschauBtnDisablesStatus(): boolean {
        return !(this.selectedBspTemplate && this.selectedBspTemplate.contentLength > 0);
    }

    formatVsnrRigid(vsnr: string) {
        return PipeFunctions.formatVsnrRigid(vsnr);
    }

    starteWeiterenAntrag(): void {
        // Parameter in der URL entfernen
        this.router.navigate(['./kreditportal/buergschaften/create-buergschein']);

        this.befristetSichtbarkeit = false;

        this.buergscheinId = null;
        this.vsnr = null;
        this.alter = null;

        this.isAenderungsantragAuswahlGesperrt = false;

        this.aenderungsBuergschaften = [];
        this.selectedBspTemplate = null;

        this.vertragsdatumEntered = false;
        this.buergschaftBeantragenBtnDisabled = false;

        // Modell zuruecksetzen
        this.model.clearModel();

        this.initialize();

        this.resetAllUploadFields();
        this.currentFileIndex = 0;
        this.numUploadFiles = 0;

        this.createState = this.STATE_1_ANTRAGSTELLER;
    }

    onVertragChanged() {
        this.updateAenderungsbuergschaftAuswahl();

        // Bei Vertragsänderung ist die Sperrung von Bürgschaftsnummer usw. hinfällig
        this.isAenderungsantragAuswahlGesperrt = false;

        this.model.aenderungsAntragBuergscheinNummer.value = '';
    }

    onMoechteAbweichendenBuergschaftstextChanged() {
        this.updateBefristet();
        this.resetAllUploadFields();
    }

    onBuergschaftsArtChanged() {
        this.validateField(this.model.buergschaftsArt);
        this.updateBuergschaftsartAbhaengigeFelder();
        this.updateBefristet();
    }

    onAuftraggeberChanged(): void {
        // Reset buergschaftsArt
        this.model.buergschaftsArt.value = '';

        this.updateBefristet();
    }

    onVertretungsformelVorhandenChanged() {
        // Validierungsnachrichten für abhängige Felder aktualisieren, wenn Wert gesetzt
        if (this.model.buergschaftsArt.value) {
            this.validateField(this.model.buergschaftsArt);
        }

        if (this.model.gewuenschtesFormular.value) {
            this.validateField(this.model.gewuenschtesFormular);
        }
    }

    onGewuenschtesFormularChanged() {
        this.updateBefristet();
        this.updateShowVersandartAuswahl();
    }

    isVertragsListeDisabled(): boolean {
        // Vertragsauswahl deaktiviert
        // - falls keine Verträge verfügbar
        // - ODER Änderungsantrag mit vorausgewähltem Vertrag
        return !this.vertragHandles
            || this.vertragHandles.length < 1
            || (this.model.typeOfAntrag.value === 'N' && this.isAenderungsantragAuswahlGesperrt);
    }

    validateField(field: ValidatorInput): boolean {
        return this.model.validateField(field);
    }

    validateVertragsdatum(validatorInput: ValidatorInput): void {
        if (this.validateField(validatorInput)) {
            this.loadBspTemplatevorschlaege();
            this.vertragsdatumEntered = true;
        } else {
            this.selectedBspTemplate = null;
            this.model.templateSpKennung.value = '';
            this.model.templateSpBeschreibung = null;
            this.vertragsdatumEntered = false;
        }
    }

    /**
     * Seite des Wizards wird geändert (vor oder zurück)
     * @param newState
     * @param force
     */
    setCreationState(newState: number, force: boolean = false): void {
        // Schritt zurück
        if (newState < this.createState) {
            this.createState = newState;
            window.scrollTo(0, 0);

            return;
        }

        // Schritt vor
        if (force && newState > this.createState) {
            const validationResult = this.model.validate(this.lastFieldsNameOfPage[newState - 1]);
            const valid = validationResult === null;

            if (valid) {
                //nur beim Wechsel von Seite 3 auf die Folgeseiten den Upload pruefen
                if (newState >= this.STATE_4_ABSCHLUSS) {
                    if (!this.hasUploadError()) {
                        this.createState = newState;
                        if (newState === this.STATE_4_ABSCHLUSS) {
                            this.model.sweepModel();
                        }
                        window.scrollTo(0, 0);
                    } else {
                        this.messageObservable.notify(new MessageModel(
                            'Bitte korrigieren Sie Ihre Angaben.',
                            ['Bitte Dateianhänge prüfen.'],
                        ));
                    }
                } else {
                    //auf die naechste Seite wechseln
                    this.createState = newState;
                    window.scrollTo(0, 0);
                }
            } else {
                this.messageObservable.notify(new MessageModel(
                    'Bitte korrigieren Sie Ihre Angaben.',
                    validationResult.map(vr => vr.message),
                ));
            }
        }
    }

    isWeitereBuergschaftszusaetze(): boolean {
        return this.weitereBuergschaftszusaetze;
    }

    isFormularUploadVisible(): boolean {
        const result = this.model.gewuenschtesFormular.value === 'SF00' &&
            (!this.selectedBspTemplate || this.model.moechteAbweichendenBuergschaftstext);

        if (!result) {
            this.resetAllUploadFields();
        }

        return result;
    }

    /**
     * Prüfe ob Bürgschaftszusätze möglich.
     * Bürgschaftszusätze nur möglich für "Formular der VHV" und Bürgschaftstypen AF, VE, MA
     * Resette ggf. die Checkboxen mit den Bürgschaftszusätzen
     */
    isGewuenschteZusaetzeMoeglich(): boolean {
        const gewuenscheZusaetzeMoeglich = this.model.gewuenschtesFormular.value === 'NEIN'
            && ['AF', 'VE', 'MA'].includes(this.model.buergschaftsArt.value);

        if (!gewuenscheZusaetzeMoeglich) {
            this.model.checkboxHinterlegung.value = '';
            this.model.checkboxBGB768.value = '';
            this.model.checkboxSGBVII150Abs3.value = '';
            this.model.checkboxSGBIV28eAbs3a.value = '';
            this.model.checkboxSicherheitseinbehalt.value = '';
            this.model.checkboxAentG14.value = '';
        }

        return gewuenscheZusaetzeMoeglich;
    }

    isFileSelected(): boolean {
        return this.fileUploadService.getFileCount() > 0;
    }

    onAuftraggeberSucheBlur() {
        this.validateField(this.model.auftraggeberName);
        this.checkForcedDigitalAuftraggeberAvailable();
    }

    onAuftraggeberPlzBlur() {
        this.validateField(this.model.auftraggeberPlz)
        this.checkForcedDigitalAuftraggeberAvailable();
    }

    onAuftraggeberSucheResultSelected(event: Auftraggeber) {
        this.setzeAuftraggeber(event);
    }

    onFileUploadSelected(fieldIndex: number, event: Event) {
        this.lastFileIndex = fieldIndex;
        this.errorMessages[fieldIndex] = null;
        this.errorMessagesMask = BuergschaftCreateComponent.bitNot(this.errorMessagesMask, fieldIndex);
        this.fileUploadService.addFiles(event, fieldIndex);
    }

    onFileUploadUnselected(fieldIndex: number) {
        this.deleteFileAt(fieldIndex);
    }

    setzeAuftraggeber(auftraggeber: Auftraggeber) {
        this.model.auftraggeberName.regex = CreateBuergscheinModel.AUFTRAGGEBER_NAME_MIT_SP_REGEX;
        this.model.auftraggeberStrasseNr.regex = CreateBuergscheinModel.AUFTRAGGEBER_STRASSE_MIT_SP_REGEX;
        this.model.auftraggeberOrt.regex = CreateBuergscheinModel.AUFTRAGGEBER_ORT_MIT_SP_REGEX;

        this.fillFieldValue(this.model.auftraggeberName, auftraggeber.name);
        this.fillFieldValue(this.model.auftraggeberStrasseNr, auftraggeber.strasse);
        this.fillFieldValue(this.model.auftraggeberPlz, auftraggeber.plz);
        this.fillFieldValue(this.model.auftraggeberOrt, auftraggeber.ort);
        this.fillFieldValue(this.model.auftraggeberLand, auftraggeber.landeskennzeichen);

        this.fillFieldValue(
            this.model.auftraggeberTyp,
            auftraggeber.vertrittOeffentlicheRessorts ? this.AGTYP_OEFFENTLICH : this.AGTYP_FIRMA
        );

        this.model.isDigitaleBuergschaft = true;
        this.model.vertrittOeffentlicheRessorts = auftraggeber.vertrittOeffentlicheRessorts;
        this.model.auftraggeberSpKennung = auftraggeber.spKennung;

        this.selectedBspTemplate = null;
        this.model.templateSpKennung.value = '';
        this.model.templateSpBeschreibung = null;
    }

    onAuftraggeberLoeschenClick() {
        this.model.auftraggeberName.regex = CreateBuergscheinModel.AUFTRAGGEBER_NAME_OHNE_SP_REGEX;
        this.model.auftraggeberStrasseNr.regex = CreateBuergscheinModel.AUFTRAGGEBER_STRASSE_OHNE_SP_REGEX;
        this.model.auftraggeberOrt.regex = CreateBuergscheinModel.AUFTRAGGEBER_ORT_OHNE_SP_REGEX;

        this.fillFieldValue(this.model.auftraggeberTyp, this.AGTYP_FIRMA);

        this.model.auftraggeberName.value = '';
        this.model.auftraggeberStrasseNr.value = '';
        this.model.auftraggeberPlz.value = '';
        this.model.auftraggeberOrt.value = '';

        this.model.isDigitaleBuergschaft = false;
        this.model.vertrittOeffentlicheRessorts = false;
        this.model.auftraggeberSpKennung = null;


        this.selectedBspTemplate = null;
        this.model.templateSpKennung.value = '';
        this.model.templateSpBeschreibung = null;
    }

    onAuftraggeberLandChanged() {
        this.validateField(this.model.auftraggeberLand);
        this.updateShowVersandartAuswahl();
    }

    getAuftraggeberFeldTitle(): string {
        return this.model.isDigitaleBuergschaft
            ? 'Da Sie einen gespeicherten Auftraggeber ausgewählt haben, ist dieses Feld für Änderungen gesperrt. Zur manuellen Anpassung den Button \"Eingabe löschen\" klicken.'
            : '';
    }

    getLabel(template: BuergschaftsTemplate) {
        switch (template.templatetyp) {
            case 'BUE':
                return 'Formular der VHV';
            case 'OEF':
                return template.beschreibungAuftraggeber;
            case 'AU':
                return 'Formular des Auftraggebers';
            default:
                throw new Error(`Templatetyp ${template.templatetyp} gibt es nicht`);
        }
    }

    get vertragHandles(): VertragHandle[] {
        return this._vertragHandles;
    }

    set vertragHandles(value: VertragHandle[]) {
        this.rigidVsnrToVertragHandleMap.clear();
        for (const vertragHandle of value) {
            this.rigidVsnrToVertragHandleMap.set(
                PipeFunctions.formatVsnrRigid(vertragHandle.vsnrFormatiert),
                vertragHandle
            );
        }

        this._vertragHandles = value;
    }

    private loadBspTemplatevorschlaege(): void {
        this.fillFieldValue(this.model.templateSpKennung, '');

        // Nur Vorschläge laden, wenn dig. AG und Vertragsdatum gesetzt sind
        if (!(this.model.auftraggeberSpKennung && this.model.vertragsDatum && this.model.vertragsDatum.value)) {
            return;
        }

        // Nur für digitale Bürgschaftsarten MA, VE, VA oder AF
        if (!(this.BUERGSCHAFTSARTEN_DIGITAL.includes(this.currFormularType.buergschaftsart))) {
            return;
        }

        this.auftraggeberApiService.templateSuche({
            trustlogAgKennung: this.model.auftraggeberSpKennung,
            buergschaftsart: this.currFormularType.buergschaftsart as any,
            vertragsdatum: this.model.vertragsDatum.value
        }).subscribe(response => {
            if (!response.templates || response.templates.length === 0) {
                // Kein digitales Bürgschaftstemplate verfügbar, resette Auswahl
                this.bspTemplates = [];
                this.fillFieldValue(this.model.templateSpKennung, '');
                this.templateUpdated();

                return;
            }

            this.bspTemplates = response.templates;

            // Wenn nur ein Trustlog-Template verfügbar ist, wähle das aus
            if (this.bspTemplates.length == 1) {
                this.fillFieldValue(this.model.templateSpKennung, this.bspTemplates[0].spKennung);
                this.templateUpdated();
            }
        });
    }

    private hasUploadError(): boolean {
        this.showNoUploadMessage = false;

        // keine Dateien hochgeladen
        if (this.isFormularUploadVisible()
            && this.fileUploadService.getFileCount() == 0
        ) {
            this.showNoUploadMessage = true;
            return true;
        }

        return this.errorMessages.find(m => m !== null) !== undefined;
    }

    private onError = (error: string): void => {
        if (error !== null) {
            // Dieser Teil wird auf der zweiten Seite an den Dateien angezeigt
            this.errorMessages[this.lastFileIndex] = error;
            this.errorMessagesMask |= (1 << this.lastFileIndex);
            this.errorMessages = this.errorMessages.slice(0);

            // Wenn auf der letzten Seite und der echte Upload hat nicht funktioniert, eine Message anzeigen
            if (this.createState === this.STATE_4_ABSCHLUSS) {
                this.errorObservable.notify(new ErrorModel(
                    ErrorSeverity.FATAL,
                    this.appConstants.httpErrorMessages._500.headline,
                    [error, this.appConstants.httpErrorMessages._500.details[0]],
                    undefined
                ));
            }
        }

        // Auch im Fehlerfall soll der "Antrag absenden" Button wieder freigegeben werden...
        this.buergschaftBeantragenBtnDisabled = false;

        // ...und die Ladeanimation beendet werden
        Animator.resolveRequest();
    };

    private onUploaded = (filename: string): void => {
        // Das Backend schickt eine Info, wie die gehashte Datei heißt; übernehme dies
        this.fileNames[this.currentFileIndex] = filename;

        ++this.currentFileIndex;
        this.done = this.currentFileIndex === this.numUploadFiles;

        if (this.done) {
            this.finishAntrag();
        }
    };

    private onAdded = (files: File[]) => {
        this.uploadedPercent = BuergschaftCreateComponent.createZeroArray(files.length);
    };

    private onProgress = (value: number): void => {
        this.uploadedPercent[this.currentFileIndex] = value;
    };

    private finishAntrag = (): void => {
        this.model.user = this.authService.getActiveKunde().vsnr;
        this.model.roleName = this.authService.getRoleName();
        this.model.activeUser = this.authService.getUserId();
        this.model.files = this.fileNames;

        if (this.selectedBspTemplate) {
            this.model.templateSpKennung.value = this.selectedBspTemplate.spKennung;
            this.model.templateSpBeschreibung = this.selectedBspTemplate.beschreibungAuftraggeber;
        }

        this.buergschaftsantragApiService.postBuergschaftsantrag(this.model.forRequest() as any)
            .subscribe(() => {
                this.createState = this.STATE_5_DANKESSEITE;

                this.buergschaftBeantragenBtnDisabled = false;
                Animator.resolveRequest();
            });
    };

    /**
     * Falls "befristet bis" ausgewählt ist, lasse nur die Auswahl befristeter Formulartypen zu.
     * Aktualisiere UI-Elemente mit Befristung
     */
    private checkBefristung() {
        // Falls "befristet bis" ausgewählt ist, lasse nur die Auswahl befristeter Formulartypen zu
        this.visibleFormulare = this.filterFormulare4ZuBefristenBis(this.getFormulare());

        if ('befristet' === this.model.frist.value) {
            // Wähle erstes erlaubtes Formular aus
            this.model.gewuenschtesFormular.value = this.currFormularType.formulare[0].left;
            this.model.gewuenschtesFormular.valid = true;
        } else {
            // Fristdatum leeren, wenn "befristet bis" wieder abgewaehlt wurde auf "unbefristet"
            this.model.fristDatum.value = '';
        }
    }

    private getFormulare(): Tuple2<string, string>[] {
        return this.currFormularType.formulare;
    }

    /**
     * Wenn die BS befristet sein soll, kann das nur für Formulare der VHV und Auftraggeberformulare der Fall sein
     * Nicht für öffentliche.
     * Gibt nur Formulartypen zurück, für die eine Befristung erlaubt ist: NEIN, SF01, VO30, SF00
     *
     * @param formulare
     */
    private filterFormulare4ZuBefristenBis(formulare: Tuple2<string, string>[]): Tuple2<string, string>[] {
        if (this.model.frist.value === 'befristet'
            && ['AF', 'VE', 'MA'].includes(this.currFormularType.buergschaftsart)
        ) {
            return formulare.filter(formular => ['NEIN', 'SF01', 'VO30', 'SF00'].includes(formular.left));
        }

        return formulare;
    }

    private resetAllUploadFields() {
        this.fileUploadService.deleteFileAtIndex(0);
        this.fileUploadService.deleteFileAtIndex(1);
        this.fileUploadService.deleteFileAtIndex(2);

        this.errorMessages = [null, null, null];
        this.errorMessagesMask = 0;
        this.fileNames = ['', '', ''];

        if (this.uploadField1) {
            this.uploadField1.unsetFileField();
        }

        // Update upload field
        const fileUploadComponents = [this.uploadField0, this.uploadField1, this.uploadField2];

        for (const fileUploadComponent of fileUploadComponents) {
            if (!fileUploadComponent) {
                continue;
            }

            fileUploadComponent.unsetFileField();
        }
    }

    private fillFieldValue(field: ValidatorInput, value: string) {
        // Setze Feldwert und triggere Validator
        field.value = value;
        this.validateField(field);
    }

    private updateAenderungsbuergschaftAuswahl() {
        this.loadBuergschaftsNummernByVertrag(this.model.vertrag.value);
    }

    private loadBuergschaftsNummernByVertrag(rigidVsnr: string): void {
        // Hole Vertragshandle (= voller Identifier) zu ausgewählter (verkürzter) Vertragsnummer
        const vertragHandle = this.rigidVsnrToVertragHandleMap.get(rigidVsnr);

        this.vertragApiService.getVertragData(vertragHandle.vsnrNumerisch)
            .subscribe(vertragResponse => {
                this.buergscheineApiService.getBuergscheinNummern(vertragResponse.vertragHandle.vsnrNumerisch)
                    .subscribe((buergscheinNummernResponse => {
                        this.aenderungsBuergschaften = buergscheinNummernResponse.buergscheinNummern;
                    }));
            });
    }

    private prepareNeuantrag() {
        // Das ist der Fall, wenn eine neue Bürgschaft beantragt werden soll
        this.userApiService.getUserData(this.authService.getUserId()).pipe(
            switchMap((userModel) => {
                const ordnungsbegriff = this.authService.determineKundennummer(this.authService.getActiveKunde().vsnr);

                // FIXME hier hat der PartnerProvider noch mehr gemacht; schauen
                return this.partnerApiService.getProfilData(ordnungsbegriff).pipe(
                    map(partnerResponse => ({ userModel, partnerResponse }))
                )
            })
        ).subscribe(({ userModel, partnerResponse }) => {
            this.model.copyUserData(partnerResponse, userModel);
            if (this.model.buergschaftsArt.value && this.model.buergschaftsArt.value.length !== 0) {
                this.currFormularType = BuergschaftsAntragTypes.findByBuergschaftsart(this.model.buergschaftsArt.value);
            }
        });
    }

    private prepareAenderungsantrag() {
        // Das ist der Fall, wenn Änderungsantrag

        this.vertraegeService.getVertraegeNummernListe().subscribe(vertragsnummern => {
            this.vertragHandles = vertragsnummern;
        });

        const vsnrNumerisch = PipeFunctions.vsnrToNumerisch(this.vsnr);

        this.vertraegeService.getVertrag(vsnrNumerisch).pipe(
            switchMap(vertrag => {
                const vsnrFormatiert = vertrag.vertragHandle.vsnrFormatiert;

                return forkJoin({
                    buergscheinLight: this.buergscheineService.getBuergscheinLight(vsnrNumerisch, this.buergscheinId),
                    buergschein: this.buergscheineService.getBuergschein(vsnrNumerisch, this.buergscheinId),
                    userData: this.userService.getCurrentUserData(),
                    partnerResponse: this.partnerService.getPartner()
                }).pipe(
                    map(results => ({ ...results, vsnrFormatiert }))
                );
            })
        ).subscribe(({ vsnrFormatiert, buergscheinLight, buergschein, userData, partnerResponse }) => {
            this.model.copyUserData(partnerResponse, userData);
            this.model.copyData(
                buergscheinLight,
                buergschein,
                vsnrFormatiert,
                this.buergscheinId,
                this.isAenderungsantrag,
            );

            if (this.model.buergschaftsArt.value) {
                this.currFormularType = BuergschaftsAntragTypes.findByBuergschaftsart(this.model.buergschaftsArt.value);
                this.onBuergschaftsArtChanged();
            }

            this.updateAenderungsbuergschaftAuswahl();
            this.updateBspDaten();
            this.updateVertragsdatum();
        });
    }

    private updateBspDaten() {
        if (!this.model.auftraggeberSpKennung) {
            return;
        }

        this.auftraggeberApiService.auftraggeberByTrustlogKennung(this.model.auftraggeberSpKennung).subscribe(auftraggeber => {
            if (!auftraggeber) {
                // Eventuell ist der Auftraggeber nicht mehr verfügbar (gelöscht aus Trustlog), in dem Fall übernehme
                // keine Daten aber zeige auch kein Fehler.
                console.warn('Der Auftraggeber konnte nicht wiederhergestellt werden.');
                this.onAuftraggeberLoeschenClick();

                return;
            }

            this.setzeAuftraggeber(auftraggeber);
            this.updateVertragsdatum();
        });
    }

    private updateVertragsdatum() {
        // Validiere das Vertragsdatum sofern gesetzt
        // Notwendig für die Vorbelegung des Felds
        // Triggert auch die Template-Suche
        if (this.model.vertragsDatum.value) {
            this.validateVertragsdatum(this.model.vertragsDatum);
        }
    }

    private ladeAuswahllisteVertraegeDaten() {
        this.vertraegeService.getVertraegeNummernListe().subscribe(vertraegeNummern => {
            this.vertragHandles = vertraegeNummern;

            // Wenn nur ein Vertrag verfügbar ist, wähle den aus
            if (this.vertragHandles.length === 1) {
                this.model.vertrag.value = this.formatVsnrRigid(this.vertragHandles[0].vsnrFormatiert);
                this.updateAenderungsbuergschaftAuswahl();
            }
        });
    }

    /**
     * Lade Liste möglicher Auftraggeber-Länder
     * @private
     */
    private ladeAuswahllisteLaenderDaten() {
        this.jsonFilesApiService.getJsonFile('laender')
            .pipe(take(1))
            .subscribe((response: any) => {
                const laender = response as Land[];

                if (!laender || laender.length === 0) {
                    throw new Error('Länderliste konnte nicht geladen werden');
                }

                this.auftraggeberLaender = laender
                    .filter((value: Land) => value.erlaubtImBSAntrag)
                    .sort((a, b) => {
                        // Sortiere primär nach Prio, aber innerhalb der gleichen Prio nach Ländernamen
                        if (a.prio === b.prio) {
                            return a.name.localeCompare(b.name);
                        }

                        return b.prio - a.prio;
                    });
            });
    }

    private deleteFileAt(index: number): void {
        this.fileUploadService.deleteFileAtIndex(index);
        this.errorMessages[index] = null;
        this.errorMessagesMask = BuergschaftCreateComponent.bitNot(this.errorMessagesMask, index);
        this.fileNames[index] = '';

        // Update upload field
        const fileUploadComponent = [this.uploadField0, this.uploadField1, this.uploadField2][index];

        if (fileUploadComponent) {
            fileUploadComponent.unsetFileField();
        }
    }

    // Incredible: the common bitnot does not compile in typescript. So here is an evaluation.
    private static bitNot(base: number, bit: number): number {
        let result = 0;

        for (let i = 0; i < 3; i++) {
            if (bit === i) {
                //continue;
            } else if ((base & 1 << i) === (1 << i)) {
                result |= 1 << i;
            }
        }

        return result;
    }

    private static createZeroArray(size: number): number[] {
        const result: number[] = [];
        for (let i = 0; i < size; i++) {
            result.push(0);
        }

        return result;
    }

    private checkForcedDigitalAuftraggeberAvailable() {
        const auftraggeberName = this.model.auftraggeberName.value;
        const auftraggeberPlz = this.model.auftraggeberPlz.value;

        if (!(auftraggeberName && auftraggeberPlz) || this.model.auftraggeberSpKennung) {
            this.model.forcedDigitalAuftraggebers = [];
            this.model.forcedAuftraggeberValidator.value = '';
            this.isForcedDigitalAuftraggeberCheckPending = false;
            return;
        }

        this.isForcedDigitalAuftraggeberCheckPending = true;

        const auftraggeberSucheParams: AuftraggeberSucheParams = {
            name: auftraggeberName,
            plz: auftraggeberPlz
        };

        this.auftraggeberApiService.auftraggeberSuche(auftraggeberSucheParams).subscribe({
            next: auftraggeberSucheResponse => {
                this.isForcedDigitalAuftraggeberCheckPending = false;

                if (!auftraggeberSucheResponse.numFoundAuftraggeber) {
                    this.model.forcedDigitalAuftraggebers = [];
                    this.model.forcedAuftraggeberValidator.value = '';
                    return;
                }

                // Backuppe die Eingaben, falls der Nutzer mit den Eingaben fortsetzen möchte
                this.forcedAuftraggeberInputBackup = {
                    spKennung: null,
                    name: auftraggeberName,
                    strasse: this.model.auftraggeberStrasseNr.value,
                    ort: this.model.auftraggeberOrt.value,
                    plz: auftraggeberPlz,
                    landeskennzeichen: this.model.auftraggeberLand.value,
                    vertrittOeffentlicheRessorts: false
                };

                this.model.forcedDigitalAuftraggebers = auftraggeberSucheResponse.auftraggeber;
                this.model.forcedAuftraggeberValidator.value = '';
            },
            error: () => {
                this.model.forcedDigitalAuftraggebers = [];
                this.model.forcedAuftraggeberValidator.value = '';
                this.isForcedDigitalAuftraggeberCheckPending = false;
            }
        });
    }

    onSelectForcedAuftraggeber(fAuftraggeber: Auftraggeber) {
        if (fAuftraggeber.spKennung) {
            this.setzeAuftraggeber(fAuftraggeber);
        } else {
            this.onAuftraggeberLoeschenClick();
            this.model.auftraggeberName.value = this.forcedAuftraggeberInputBackup.name;
            this.model.auftraggeberStrasseNr.value = this.forcedAuftraggeberInputBackup.strasse;
            this.model.auftraggeberOrt.value = this.forcedAuftraggeberInputBackup.ort;
            this.model.auftraggeberPlz.value = this.forcedAuftraggeberInputBackup.plz;
            this.model.auftraggeberLand.value = this.forcedAuftraggeberInputBackup.landeskennzeichen;
        }
    }

}

export default BuergschaftCreateComponent;
